import React from "react";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ExpenseReportData } from "../types";
import {
  formatCurrency,
  formatDate,
  formatText,
  formatVehicleNumber,
} from "../../../utils/utils";
import { useNavigate } from "react-router-dom";

interface DetailedExpensesProps {
  data: ExpenseReportData;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const DetailedExpenses: React.FC<DetailedExpensesProps> = ({
  data,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { results, pagination } = data;
  const totalPages = pagination?.totalPages || 1;
  const navigate = useNavigate();
  // Pagination handlers
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    onPageChange(page);
  };

  const handlePageSizeChange = (event: any) => {
    const newPageSize = event.target.value;
    onPageSizeChange(newPageSize);
  };

  // Function to get dynamic detail based on expense type
  const getExpenseDetail = (expense: any) => {
    const {
      expense_type,
      paid_to,
      location,
      vehicle_number,
      fuel_type,
      liters,
      price_per_litre,
      service_type,
      service_provider_name,
      challan_type,
      challan_number,
      staff_name,
      staff_contact,
    } = expense;

    switch (expense_type) {
      case "FUEL":
        return `${fuel_type || "N/A"} - ${liters || "0"}L @ ₹${
          price_per_litre || "0"
        }/L`;

      case "REPAIR":
        return `${service_type || "N/A"} - ${service_provider_name || "N/A"}`;

      case "DRIVER_CLEANER":
        return `${staff_name || "N/A"}`;

      case "CHALLAN":
        return `${challan_type || "N/A"} - ${challan_number || "N/A"}`;

      case "PARKING":
        return `${paid_to || "N/A"} - ${location || "N/A"}`;

      case "PICKUP_MAN":
        return `${staff_name || "N/A"}`;

      case "OTHER":
        return `${location || "N/A"}`;

      default:
        return `${paid_to || "N/A"} - ${location || "N/A"}`;
    }
  };

  const getExpenseType = (expense: any) => {
    if (expense.expense_type === "OTHER") {
      return expense.paid_to;
    }
    return formatText(expense.expense_type);
  };

  const EXPENSE_CATEGORIES = {
    TRIP_EXPENSE: "Daily Service",
    VEHICLE_EXPENSE: "Vehicle",
    PROJECT_EXPENSE: "Contract",
    RENTAL_EXPENSE: "Booking",
  };

  const handleViewDetail = (expense: any) => {
    // based on master_category type navigate to different pages
    if (expense.master_category === "TRIP_EXPENSE") {
      navigate(`/trip/daily-bus/${expense.master_reference_id}/expense`);
    } else if (expense.master_category === "VEHICLE_EXPENSE") {
      navigate(`/vehicle-expense/edit/${expense.expense_id}`);
    } else if (expense.master_category === "PROJECT_EXPENSE") {
      navigate(
        `/project-expense/edit/${expense.master_reference_id}/${expense.expense_id}`
      );
    } else if (expense.master_category === "RENTAL_EXPENSE") {
      let category = expense.acquisition_type;
      if (category === "Outsourced") {
        category = "OUTSOURCED";
      } else {
        category = "OWNED";
      }
      navigate(
        `/rental/${expense.master_reference_id}/logs?type=${category}&tab=EXPENSE&vehicleId=${expense.vehicle_id}&vehicleNumber=${expense.vehicle_number}`
      );
    }
  };

  if (results.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "center", display: "block", py: 3 }}
        >
          No expense records available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography fontSize={14} fontWeight={600} mb={2}>
              Detailed Expenses
            </Typography>

            <TableContainer
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Vehicle
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Detail
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        py: 1,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Balance
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((expense, index) => {
                    // Check if this is the first occurrence of this date
                    const isFirstOccurrenceOfDate =
                      index === 0 ||
                      results[index - 1]?.expense_date !==
                        expense?.expense_date;

                    return (
                      <>
                        {isFirstOccurrenceOfDate && (
                          <Box sx={{ py: 0.8, px: 1 }}>
                            <Typography fontSize={10} fontWeight={700}>
                              {formatDate(expense.expense_date)}
                            </Typography>{" "}
                          </Box>
                        )}
                        <TableRow key={expense.expense_id}>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                              },
                              textDecoration: "underline",
                            }}
                            onClick={() => handleViewDetail(expense)}
                          >
                            {formatDate(expense.expense_date)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                            }}
                          >
                            {getExpenseType(expense)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                            }}
                          >
                            {EXPENSE_CATEGORIES[expense.master_category as keyof typeof EXPENSE_CATEGORIES] || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                            }}
                          >
                            {formatVehicleNumber(expense.vehicle_number) || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                              maxWidth: "200px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getExpenseDetail(expense)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(parseFloat(expense.amount))}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 500,
                            }}
                          >
                            {expense.payment_amount
                              ? formatCurrency(
                                  parseFloat(expense.payment_amount)
                                )
                              : "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 10,
                              py: 0.8,
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(
                              parseFloat(expense.outstanding_amount)
                            )}
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 80, fontSize: "0.75rem" }}
                  >
                    <Select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      displayEmpty
                      size="small"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                        20
                      </MenuItem>
                      <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                        50
                      </MenuItem>
                      <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                        100
                      </MenuItem>
                      <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                        200
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    {`${(pagination.currentPage - 1) * pageSize + 1}-${Math.min(
                      pagination.currentPage * pageSize,
                      pagination.totalRecords
                    )} of ${pagination.totalRecords}`}
                  </Typography>
                </Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetailedExpenses;
